import { useState, useContext, useEffect } from 'react';
import { Box, Button, Stack, Badge } from '@mui/material';
import TocIcon from '@mui/icons-material/Toc';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ThemeContext } from '../Context';
import { SocketContext } from '../Context';
import { GuardiaContext } from '../Context';
import Summary from './Summary';
import Chat from './Chat';
import MyInsertions from './MyInsertions';
import GuardiaRight from './GuardiaRight';

// Partie droite (right) de l'application, elle affiche l'état actuel du procès-verbal modifié.
function Right() {
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const { socket } = useContext(SocketContext);
  const { theme } = useContext(ThemeContext);
  const { activeView, changeActiveView } = useContext(GuardiaContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen();

    return () => observer.disconnect();
  }, []);

  // Écouter les nouveaux messages
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (data) => {
      // Si l'utilisateur n'est pas sur la page chat, marquer comme non lu
      if (activeView !== 'chat') {
        setHasUnreadMessages(true);
      }
    };

    socket.on('newMessage', handleNewMessage);

    return () => {
      socket.off('newMessage', handleNewMessage);
    };
  }, [socket, activeView]);

  const handleViewChange = (view) => {
    changeActiveView(view);

    // Si l'utilisateur clique sur Discussion, marquer les messages comme lus
    if (view === 'chat') {
      setHasUnreadMessages(false);
    }
  };

  // Ne pas afficher quand TinyMCE est en mode fullscreen
  if (isEditorFullscreen) {
    return null;
  }

  // Style commun des onglets ; accent optionnel (GuardIA en violet)
  const tabSx = (id, accent) => {
    const isActive = activeView === id;
    const activeColor = accent || 'primary.main';
    return {
      flex: 1,
      minWidth: 0,
      textTransform: 'none',
      fontWeight: isActive ? 700 : 500,
      py: 1,
      fontSize: '0.875rem',
      whiteSpace: 'nowrap',
      color: accent
        ? accent
        : (isActive ? 'primary.main' : (isDark ? '#bdc3c7' : '#666')),
      backgroundColor: isActive
        ? (isDark ? '#1a252f' : '#f5f5f5')
        : 'transparent',
      borderRadius: 0,
      position: 'relative',
      '& .MuiButton-startIcon': {
        mr: 0.5,
        ml: 0
      },
      // Masquer les icônes quand la colonne devient trop étroite
      '@container (max-width: 300px)': {
        '& .MuiButton-startIcon': {
          display: 'none'
        }
      },
      '&:hover': {
        backgroundColor: isActive
          ? (isDark ? '#1a252f' : '#f5f5f5')
          : (accent
            ? (isDark ? 'rgba(128, 0, 128, 0.15)' : 'rgba(128, 0, 128, 0.08)')
            : (isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(255, 255, 255, 0.6)')),
        color: activeColor,
      },
      transition: 'all 0.2s ease',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '3px',
        backgroundColor: activeColor,
        transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
        transition: 'transform 0.25s ease',
      },
    };
  };

  const guardiaAccent = isDark ? '#c084fc' : '#800080';

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Barre de boutons fixée en haut */}
      <Box
        sx={{
          flexShrink: 0, // Empêche la barre de boutons de rétrécir
          zIndex: 10
        }}
      >
        <Stack 
          direction="row" 
          spacing={0} 
          sx={{ 
            background: isDark 
              ? 'rgb(40, 57, 73)' 
              : 'rgb(218, 230, 236)',
            boxShadow: isDark 
              ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
              : '0 2px 8px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            containerType: 'inline-size',
          }}
        >
          <Button
            onClick={() => handleViewChange('summary')}
            startIcon={<TocIcon sx={{ fontSize: '16px !important' }} />}
            sx={tabSx('summary')}
          >
            Sommaire
          </Button>

          <Button
            onClick={() => handleViewChange('myInsertions')}
            startIcon={<PostAddIcon sx={{ fontSize: '16px !important' }} />}
            sx={tabSx('myInsertions')}
          >
            Insertions
          </Button>

          <Badge
            badgeContent=""
            variant="dot"
            color="error"
            invisible={!hasUnreadMessages}
            sx={{
              flex: 1, // Assure que le Badge prend la même largeur que les autres boutons
              '& .MuiBadge-dot': {
                minWidth: '10px',
                height: '10px',
                borderRadius: '50%',
                animation: hasUnreadMessages ? 'pulse 2s infinite' : 'none',
              },
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.2)',
                  opacity: 0.7,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            }}
          >
            <Button
              onClick={() => handleViewChange('chat')}
              startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                ...tabSx('chat'),
                width: '100%', // Le bouton prend toute la largeur du Badge
                flex: 'none',
              }}
            >
              Discussion
            </Button>
          </Badge>

          <Button
            onClick={() => handleViewChange('guardia')}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: '16px !important' }} />}
            sx={tabSx('guardia', guardiaAccent)}
          >
            GuardIA
          </Button>
        </Stack>
      </Box>

      {/* Zone de contenu défilable */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          backgroundColor: isDark ? '#1a252f' : 'inherit',
          // Styles personnalisés pour les scrollbars en mode sombre
          '&::-webkit-scrollbar': {
            width: isDark ? '8px' : undefined,
            height: isDark ? '8px' : undefined,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: isDark ? '#22303e' : undefined,
            borderRadius: isDark ? '4px' : undefined,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? '#2d3f4c' : undefined,
            borderRadius: isDark ? '4px' : undefined,
            '&:hover': {
              backgroundColor: isDark ? '#384550' : undefined,
            },
          },
          // Pour Firefox
          scrollbarWidth: isDark ? 'thin' : undefined,
          scrollbarColor: isDark ? '#2d3f4c #22303e' : undefined,
        }}
      >
        {/* Tous les composants sont toujours montés pour préserver les états, mais masqués visuellement */}
        <Box sx={{ display: activeView === 'summary' ? 'block' : 'none', height: '100%' }}>
          <Summary />
        </Box>
        <Box sx={{ display: activeView === 'chat' ? 'block' : 'none', height: '100%' }}>
          <Chat />
        </Box>
        <Box sx={{ display: activeView === 'myInsertions' ? 'block' : 'none', height: '100%' }}>
          <MyInsertions />
        </Box>
        <Box sx={{ display: activeView === 'guardia' ? 'block' : 'none', height: '100%' }}>
          <GuardiaRight />
        </Box>
      </Box>
    </Box>
  );
}

export default Right;



